(() => {
  const body = document.body;
  const modal = document.querySelector('[data-region-modal]');
  if (!modal) return;
  const panel = modal.querySelector('[role="dialog"]');
  let returnFocus = null;

  const open = (trigger) => {
    returnFocus = trigger;
    modal.hidden = false;
    body.classList.add('fs-region-open');
    window.requestAnimationFrame(() => panel?.focus());
  };
  const close = () => {
    modal.hidden = true;
    body.classList.remove('fs-region-open');
    returnFocus?.focus();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-region-open]');
    if (opener) {
      event.preventDefault();
      open(opener);
      return;
    }
    if (event.target.closest('[data-region-close]')) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) close();
  });
})();

/* Typography-only bridge for the Shopify Forms app block on the Contact page.
   Shopify Forms can render its visual controls inside open Shadow DOM. This
   bridge adds CSS only; it never reads or changes Form IDs, values, events,
   submission behavior or file uploads. */
(() => {
  const styleId = 'flatsons-contact-form-style-v261';
  const contactFormCss = `
    :host { text-align: left !important; }
    h1, h2, h3, [role="heading"] {
      margin: 0 0 6px !important;
      font-family: var(--display, Arial, sans-serif) !important;
      font-size: 20px !important;
      font-weight: 700 !important;
      line-height: 1.2 !important;
      letter-spacing: .04em !important;
      text-align: left !important;
    }
    p {
      font-size: 14px !important;
      font-weight: 400 !important;
      line-height: 1.5 !important;
      text-align: left !important;
    }
    h1 + p, h2 + p, h3 + p, [role="heading"] + p { margin: 0 0 28px !important; }
    form { row-gap: 14px !important; }
    label {
      font-size: 14px !important;
      font-weight: 500 !important;
      line-height: 1.35 !important;
      text-align: left !important;
    }
    input:not([type="checkbox"]):not([type="radio"]):not([type="file"]), select {
      height: 48px !important;
      min-height: 48px !important;
      font-size: 14px !important;
      font-weight: 400 !important;
    }
    textarea {
      min-height: 104px !important;
      font-size: 14px !important;
      font-weight: 400 !important;
      line-height: 1.5 !important;
    }
    input::placeholder, textarea::placeholder {
      font-size: 14px !important;
      font-weight: 400 !important;
      opacity: 1 !important;
    }
    button { font-size: 14px !important; font-weight: 500 !important; }
    [class*="field"], [class*="Field"], [class*="form-group"], [class*="FormGroup"] {
      row-gap: 14px !important;
    }
    @media (max-width: 620px) {
      h1, h2, h3, [role="heading"] { font-size: 19px !important; }
    }
  `;

  const addStyle = (root) => {
    if (!root || root.querySelector?.(`#${styleId}`)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = contactFormCss;
    root.prepend(style);
  };

  const styleOpenShadowRoots = (scope = document) => {
    scope.querySelectorAll?.('.contact-page .fs-contact-app-form').forEach((contactRoot) => {
      const visit = (root) => {
        root.querySelectorAll?.('*').forEach((node) => {
          if (node.shadowRoot) {
            addStyle(node.shadowRoot);
            visit(node.shadowRoot);
          }
        });
      };
      visit(contactRoot);
    });
  };

  const initContactFormStyle = () => {
    styleOpenShadowRoots();
    let attempts = 0;
    const timer = window.setInterval(() => {
      styleOpenShadowRoots();
      attempts += 1;
      if (attempts >= 30) window.clearInterval(timer);
    }, 500);
  };

  initContactFormStyle();
  document.addEventListener('shopify:section:load', initContactFormStyle);
})();

(() => {
  const closeSearch = (form) => {
    form?.classList.remove('is-open');
    form?.querySelector('[data-home-search-input]')?.setAttribute('aria-expanded', 'false');
  };

  const filterRecommendations = (form) => {
    const input = form.querySelector('[data-home-search-input]');
    const query = input?.value.trim().toLowerCase() || '';
    const items = [...form.querySelectorAll('[data-home-recommendation]')];
    let visible = 0;

    items.forEach((item) => {
      const match = query === '' || (item.dataset.search || item.textContent || '').toLowerCase().includes(query);
      item.hidden = !match;
      if (match) visible += 1;
    });

    const count = form.querySelector('[data-home-search-count]');
    const empty = form.querySelector('[data-home-search-empty]');
    if (count) count.textContent = `${visible}/${items.length}`;
    if (empty) empty.hidden = visible !== 0;
    form.classList.add('is-open');
    input?.setAttribute('aria-expanded', 'true');
  };

  const initSearch = (scope = document) => {
    scope.querySelectorAll('[data-home-search-form]').forEach((form) => {
      if (form.dataset.homeSearchReady === 'true') return;
      form.dataset.homeSearchReady = 'true';
      const input = form.querySelector('[data-home-search-input]');
      input?.addEventListener('focus', () => filterRecommendations(form));
      input?.addEventListener('input', () => filterRecommendations(form));
      input?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeSearch(form);
          input.blur();
        }
      });
    });
  };

  const closeHeaderSearch = (root, restoreFocus = true) => {
    const overlay = root?.querySelector('[data-header-search-overlay]');
    const trigger = root?.querySelector('[data-header-search-open]');
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    trigger?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('fs-header-search-open');
    closeSearch(root.querySelector('[data-home-search-form]'));
    if (restoreFocus) trigger?.focus();
  };

  const openHeaderSearch = (root) => {
    const overlay = root.querySelector('[data-header-search-overlay]');
    const trigger = root.querySelector('[data-header-search-open]');
    const form = root.querySelector('[data-home-search-form]');
    const input = form?.querySelector('[data-home-search-input]');
    if (!overlay) return;
    document.querySelectorAll('[data-header-search]').forEach((otherRoot) => {
      if (otherRoot !== root) closeHeaderSearch(otherRoot, false);
    });
    overlay.hidden = false;
    trigger?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('fs-header-search-open');
    if (form) filterRecommendations(form);
    window.requestAnimationFrame(() => input?.focus());
  };

  const initHeaderSearch = (scope = document) => {
    const roots = [...scope.querySelectorAll('[data-header-search]')];
    if (scope.matches?.('[data-header-search]')) roots.unshift(scope);
    roots.forEach((root) => {
      if (root.dataset.headerSearchReady === 'true') return;
      root.dataset.headerSearchReady = 'true';
      const trigger = root.querySelector('[data-header-search-open]');
      trigger?.addEventListener('click', () => {
        const overlay = root.querySelector('[data-header-search-overlay]');
        if (overlay?.hidden) openHeaderSearch(root);
        else closeHeaderSearch(root);
      });
      root.querySelectorAll('[data-header-search-close]').forEach((button) => {
        button.addEventListener('click', () => closeHeaderSearch(root));
      });
    });
  };

  initSearch();
  initHeaderSearch();
  document.addEventListener('shopify:section:load', (event) => {
    initSearch(event.target);
    initHeaderSearch(event.target);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-header-search]').forEach((root) => closeHeaderSearch(root));
  });
  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-home-search-form].is-open').forEach((form) => {
      if (!form.contains(event.target)) closeSearch(form);
    });
  });
})();
